import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BookOpenIcon,
  UserIcon,
  ClockIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  getBorrowings,
  getOverdueBooks,
  borrowBook,
  borrowBookByBarcode,
  returnBook,
  returnBookByBarcode,
  renewBorrowing,
  markBookAsLost,
  getBookByBarcode,
  getUserByRFIDCard,
  type Borrowing,
  type Book,
  type BookCopy,
  type BorrowingStatus,
} from '@/api/libraryApi'
import { getUsers, type User } from '@/api/usersApi'
import toast from 'react-hot-toast'

const BorrowingsPage: React.FC = () => {
  const { user } = useAuth()
  const [borrowings, setBorrowings] = useState<Borrowing[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showBorrowModal, setShowBorrowModal] = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [selectedBorrowing, setSelectedBorrowing] = useState<Borrowing | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [overdueCount, setOverdueCount] = useState(0)
  const pageSize = 20

  // Borrow modal state
  const [borrowBarcode, setBorrowBarcode] = useState('')
  const [scannedBook, setScannedBook] = useState<any>(null)
  const [selectedBorrower, setSelectedBorrower] = useState<User | null>(null)
  const [borrowerSearch, setBorrowerSearch] = useState('')
  const [borrowerResults, setBorrowerResults] = useState<User[]>([])
  const [borrowerType, setBorrowerType] = useState<'student' | 'teacher' | 'staff'>('student')
  const [rfidCardInput, setRfidCardInput] = useState('')
  const [rfidSearching, setRfidSearching] = useState(false)

  // Return modal state
  const [returnBarcode, setReturnBarcode] = useState('')
  const [returnCondition, setReturnCondition] = useState<string>('good')

  // Search state
  const [searchInput, setSearchInput] = useState('')
  const [searchType, setSearchType] = useState<'all' | 'rfid' | 'barcode'>('all')
  const [searchingBorrowings, setSearchingBorrowings] = useState(false)
  const [filteredBorrower, setFilteredBorrower] = useState<any>(null)
  const [filteredBook, setFilteredBook] = useState<any>(null)

  // Lost book modal state
  const [showLostModal, setShowLostModal] = useState(false)
  const [borrowingToMarkLost, setBorrowingToMarkLost] = useState<Borrowing | null>(null)
  const [markingAsLost, setMarkingAsLost] = useState(false)

  useEffect(() => {
    fetchBorrowings()
    fetchOverdueCount()
  }, [currentPage, statusFilter])

  const fetchBorrowings = async () => {
    try {
      setLoading(true)
      const response = await getBorrowings({
        page: currentPage,
        limit: pageSize,
        status: statusFilter !== 'all' ? (statusFilter as BorrowingStatus) : undefined,
        overdue: statusFilter === 'overdue' ? true : undefined,
      })
      setBorrowings(response.data)
      setTotalPages(response.pagination.pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load borrowings')
    } finally {
      setLoading(false)
    }
  }

  const fetchOverdueCount = async () => {
    try {
      const response = await getOverdueBooks({ limit: 1 })
      setOverdueCount(response.pagination.total)
    } catch (error) {
      console.error('Failed to fetch overdue count:', error)
    }
  }

  // Search borrowings by RFID card or book barcode
  const handleSearchBorrowings = async () => {
    if (!searchInput.trim()) {
      clearSearch()
      return
    }

    setSearchingBorrowings(true)
    try {
      // Try RFID card first
      if (searchType === 'all' || searchType === 'rfid') {
        try {
          const rfidResponse = await getUserByRFIDCard(searchInput.trim())
          if (rfidResponse.success && rfidResponse.data) {
            setFilteredBorrower(rfidResponse.data)
            setFilteredBook(null)
            // Filter borrowings by this user
            const userBorrowings = borrowings.filter((b: any) => {
              const borrower = typeof b.borrower === 'object' ? b.borrower : null
              return borrower && (borrower._id === rfidResponse.data._id || borrower.id === rfidResponse.data._id)
            })
            if (userBorrowings.length > 0) {
              setBorrowings(userBorrowings)
              toast.success(`Found ${userBorrowings.length} borrowing(s) for ${rfidResponse.data.firstName} ${rfidResponse.data.lastName}`)
            } else {
              toast.success(`${rfidResponse.data.firstName} ${rfidResponse.data.lastName} has no active borrowings`)
            }
            setSearchingBorrowings(false)
            return
          }
        } catch (rfidError) {
          // Not an RFID card, continue to barcode search
        }
      }

      // Try book barcode
      if (searchType === 'all' || searchType === 'barcode') {
        try {
          const bookResponse = await getBookByBarcode(searchInput.trim())
          if (bookResponse.data) {
            setFilteredBook(bookResponse.data)
            setFilteredBorrower(null)
            // Filter borrowings by this book
            const bookData = bookResponse.data
            const bookBorrowings = borrowings.filter((b: any) => {
              const book = typeof b.book === 'object' ? b.book : null
              const bookCopy = typeof b.bookCopy === 'object' ? b.bookCopy : null
              return (
                (book && (book._id === bookData.book?._id || book._id === bookData._id)) ||
                (bookCopy && bookCopy.barcode === searchInput.trim())
              )
            })
            if (bookBorrowings.length > 0) {
              setBorrowings(bookBorrowings)
              toast.success(`Found ${bookBorrowings.length} borrowing(s) for "${bookData.book?.title || bookData.title}"`)
            } else {
              toast.success(`"${bookData.book?.title || bookData.title}" has no active borrowings`)
            }
            setSearchingBorrowings(false)
            return
          }
        } catch (bookError) {
          // Not a valid barcode
        }
      }

      toast.error('No borrower or book found with this input')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Search failed')
    } finally {
      setSearchingBorrowings(false)
    }
  }

  const clearSearch = () => {
    setSearchInput('')
    setFilteredBorrower(null)
    setFilteredBook(null)
    fetchBorrowings()
  }

  const handleScanBook = async () => {
    if (!borrowBarcode.trim()) return
    try {
      const response = await getBookByBarcode(borrowBarcode.trim())
      setScannedBook(response.data)
      toast.success('Book found!')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Book not found')
      setScannedBook(null)
    }
  }

  const searchBorrowers = async (query: string) => {
    if (query.length < 2) {
      setBorrowerResults([])
      return
    }
    try {
      const response = await getUsers({ search: query, limit: 10 })
      setBorrowerResults(response.users || [])
    } catch (error) {
      console.error('Failed to search users:', error)
      setBorrowerResults([])
    }
  }

  // Search by RFID card number
  const handleRFIDCardScan = async () => {
    if (!rfidCardInput.trim()) return
    setRfidSearching(true)
    try {
      const response = await getUserByRFIDCard(rfidCardInput.trim())
      if (response.success && response.data) {
        const userData = response.data
        setSelectedBorrower({
          _id: userData._id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          studentId: userData.studentId,
        } as any)
        setBorrowerSearch(`${userData.firstName} ${userData.lastName}`)
        setBorrowerResults([])
        // Auto-detect borrower type
        if (userData.assignedToType === 'student') setBorrowerType('student')
        else if (userData.assignedToType === 'teacher') setBorrowerType('teacher')
        else setBorrowerType('staff')
        toast.success(`Found: ${userData.firstName} ${userData.lastName}`)
        setRfidCardInput('')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Card not found or not assigned')
    } finally {
      setRfidSearching(false)
    }
  }

  const handleBorrowBook = async () => {
    if (!scannedBook || !selectedBorrower) {
      toast.error('Please scan a book and select a borrower')
      return
    }

    try {
      let copyId: string
      if (scannedBook.scannedType === 'copy') {
        copyId = scannedBook.copy._id
      } else {
        // Find an available copy
        const availableCopy = scannedBook.copies?.find((c: BookCopy) => c.status === 'available')
        if (!availableCopy) {
          toast.error('No available copies of this book')
          return
        }
        copyId = availableCopy._id
      }

      await borrowBook({
        bookCopyId: copyId,
        borrowerId: (selectedBorrower as any)._id || (selectedBorrower as any).id,
        borrowerType,
      })
      toast.success('Book borrowed successfully!')
      setShowBorrowModal(false)
      resetBorrowModal()
      fetchBorrowings()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to borrow book')
    }
  }

  const handleReturnBook = async () => {
    if (!returnBarcode.trim()) {
      toast.error('Please scan or enter the book barcode')
      return
    }

    try {
      const response = await returnBookByBarcode({
        barcode: returnBarcode.trim(),
        conditionAtReturn: returnCondition as any,
        createFineIfDamaged: ['damaged', 'poor'].includes(returnCondition),
      })
      toast.success(response.message || 'Book returned successfully!')
      setShowReturnModal(false)
      setReturnBarcode('')
      setReturnCondition('good')
      fetchBorrowings()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to return book')
    }
  }

  const handleReturnBorrowing = async (borrowing: Borrowing) => {
    try {
      const response = await returnBook(borrowing._id, {
        conditionAtReturn: 'good',
      })
      toast.success(response.message || 'Book returned successfully!')
      fetchBorrowings()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to return book')
    }
  }

  const handleRenewBorrowing = async (borrowing: Borrowing) => {
    try {
      const response = await renewBorrowing(borrowing._id)
      toast.success(response.message || 'Borrowing renewed successfully!')
      fetchBorrowings()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to renew borrowing')
    }
  }

  const openLostModal = (borrowing: Borrowing) => {
    setBorrowingToMarkLost(borrowing)
    setShowLostModal(true)
  }

  const handleMarkAsLost = async () => {
    if (!borrowingToMarkLost) return
    setMarkingAsLost(true)
    try {
      await markBookAsLost(borrowingToMarkLost._id)
      toast.success('Book marked as lost. Fine has been issued.')
      setShowLostModal(false)
      setBorrowingToMarkLost(null)
      fetchBorrowings()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to mark book as lost')
    } finally {
      setMarkingAsLost(false)
    }
  }

  const resetBorrowModal = () => {
    setBorrowBarcode('')
    setScannedBook(null)
    setSelectedBorrower(null)
    setBorrowerSearch('')
    setBorrowerResults([])
    setBorrowerType('student')
    setRfidCardInput('')
  }

  const getStatusColor = (status: BorrowingStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700'
      case 'renewed':
        return 'bg-blue-100 text-blue-700'
      case 'overdue':
        return 'bg-red-100 text-red-700'
      case 'returned':
        return 'bg-gray-100 text-gray-700'
      case 'lost':
        return 'bg-amber-100 text-amber-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const getDaysUntilDue = (dueDate: string) => {
    const due = new Date(dueDate)
    const today = new Date()
    const diffTime = due.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Borrowings Management</h1>
          <p className="text-gray-500 mt-1">
            {overdueCount > 0 && (
              <span className="text-red-600 font-medium">{overdueCount} overdue</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowReturnModal(true)}
            className="flex items-center gap-2"
          >
            <ArrowUturnLeftIcon className="w-5 h-5" />
            Return Book
          </Button>
          <Button onClick={() => setShowBorrowModal(true)} className="flex items-center gap-2">
            <BookOpenIcon className="w-5 h-5" />
            Borrow Book
          </Button>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchBorrowings()}
                placeholder="Scan RFID card or book barcode..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
            </div>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Auto-detect</option>
              <option value="rfid">RFID Card</option>
              <option value="barcode">Book Barcode</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSearchBorrowings}
              disabled={searchingBorrowings || !searchInput.trim()}
              className="flex items-center gap-2"
            >
              <QrCodeIcon className="w-5 h-5" />
              {searchingBorrowings ? 'Searching...' : 'Search'}
            </Button>
            {(filteredBorrower || filteredBook) && (
              <Button variant="outline" onClick={clearSearch} className="flex items-center gap-2">
                <XMarkIcon className="w-5 h-5" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Active Filter Display */}
        {(filteredBorrower || filteredBook) && (
          <div className="mt-3 flex items-center gap-2">
            {filteredBorrower && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm">
                <UserIcon className="w-4 h-4 text-blue-600" />
                <span className="text-blue-800">
                  Showing borrowings for: <strong>{filteredBorrower.firstName} {filteredBorrower.lastName}</strong>
                </span>
              </div>
            )}
            {filteredBook && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-sm">
                <BookOpenIcon className="w-4 h-4 text-purple-600" />
                <span className="text-purple-800">
                  Showing borrowings for: <strong>{filteredBook.book?.title || filteredBook.title}</strong>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => {
            clearSearch()
            setStatusFilter('active')
            setCurrentPage(1)
          }}
          className={`p-4 rounded-xl border ${
            statusFilter === 'active' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BookOpenIcon className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-lg font-semibold text-gray-900">
                {borrowings.filter((b) => b.status === 'active').length}
              </p>
            </div>
          </div>
        </button>
        <button
          onClick={() => {
            clearSearch()
            setStatusFilter('overdue')
            setCurrentPage(1)
          }}
          className={`p-4 rounded-xl border ${
            statusFilter === 'overdue' ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-500">Overdue</p>
              <p className="text-lg font-semibold text-gray-900">{overdueCount}</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => {
            clearSearch()
            setStatusFilter('renewed')
            setCurrentPage(1)
          }}
          className={`p-4 rounded-xl border ${
            statusFilter === 'renewed' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowPathIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-500">Renewed</p>
              <p className="text-lg font-semibold text-gray-900">
                {borrowings.filter((b) => b.status === 'renewed').length}
              </p>
            </div>
          </div>
        </button>
        <button
          onClick={() => {
            clearSearch()
            setStatusFilter('all')
            setCurrentPage(1)
          }}
          className={`p-4 rounded-xl border ${
            statusFilter === 'all' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <CheckCircleIcon className="w-5 h-5 text-primary-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-500">All</p>
              <p className="text-lg font-semibold text-gray-900">View All</p>
            </div>
          </div>
        </button>
      </div>

      {/* Borrowings Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Book
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Borrower
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Borrowed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {borrowings.map((borrowing) => {
                    const book = typeof borrowing.book === 'object' ? borrowing.book : null
                    const borrower =
                      typeof borrowing.borrower === 'object' ? borrowing.borrower : null
                    const bookCopy =
                      typeof borrowing.bookCopy === 'object' ? borrowing.bookCopy : null
                    const daysUntilDue = getDaysUntilDue(borrowing.dueDate)
                    const isOverdue = daysUntilDue < 0

                    return (
                      <tr key={borrowing._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-12 bg-gray-100 rounded flex items-center justify-center">
                              {(book as any)?.coverImage ? (
                                <img
                                  src={(book as any).coverImage}
                                  alt=""
                                  className="w-full h-full object-cover rounded"
                                />
                              ) : (
                                <BookOpenIcon className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                                {(book as any)?.title || 'Unknown Book'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(bookCopy as any)?.barcode || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              {(borrower as any)?.profileImage ? (
                                <img
                                  src={(borrower as any).profileImage}
                                  alt=""
                                  className="w-full h-full object-cover rounded-full"
                                />
                              ) : (
                                <UserIcon className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {borrower
                                  ? `${(borrower as any).firstName} ${(borrower as any).lastName}`
                                  : 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {borrowing.borrowerType}
                                {(borrower as any)?.studentId && ` • ${(borrower as any).studentId}`}
                              </p>
                              {(borrower as any)?.phone && (
                                <p className="text-xs text-gray-500">{(borrower as any).phone}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {new Date(borrowing.borrowedDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm text-gray-900">
                              {new Date(borrowing.dueDate).toLocaleDateString()}
                            </p>
                            {borrowing.status !== 'returned' && borrowing.status !== 'lost' && (
                              <p
                                className={`text-xs ${
                                  isOverdue
                                    ? 'text-red-600'
                                    : daysUntilDue <= 3
                                    ? 'text-amber-600'
                                    : 'text-gray-500'
                                }`}
                              >
                                {isOverdue
                                  ? `${Math.abs(daysUntilDue)} days overdue`
                                  : `${daysUntilDue} days left`}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              borrowing.status
                            )}`}
                          >
                            {borrowing.status}
                          </span>
                          {borrowing.renewalCount > 0 && (
                            <span className="ml-1 text-xs text-gray-400">
                              ({borrowing.renewalCount}/{borrowing.maxRenewals})
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          {['active', 'renewed', 'overdue'].includes(borrowing.status) && (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleReturnBorrowing(borrowing)}
                                className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100"
                                title="Return"
                              >
                                Return
                              </button>
                              {borrowing.renewalCount < borrowing.maxRenewals && !isOverdue && (
                                <button
                                  onClick={() => handleRenewBorrowing(borrowing)}
                                  className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                                  title="Renew"
                                >
                                  Renew
                                </button>
                              )}
                              <button
                                onClick={() => openLostModal(borrowing)}
                                className="px-2 py-1 text-xs font-medium text-amber-600 bg-amber-50 rounded hover:bg-amber-100"
                                title="Mark as Lost"
                              >
                                Lost
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {borrowings.length === 0 && (
              <div className="text-center py-12">
                <BookOpenIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No borrowings found</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Borrow Book Modal */}
      <AnimatePresence>
        {showBorrowModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowBorrowModal(false)
              resetBorrowModal()
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Borrow Book</h2>
                <button
                  onClick={() => {
                    setShowBorrowModal(false)
                    resetBorrowModal()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Step 1: Scan Book */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Step 1: Scan or Enter Book Barcode
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={borrowBarcode}
                      onChange={(e) => setBorrowBarcode(e.target.value)}
                      placeholder="Enter barcode..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleScanBook()}
                    />
                    <Button onClick={handleScanBook}>
                      <QrCodeIcon className="w-5 h-5 mr-2" />
                      Scan
                    </Button>
                  </div>
                  {scannedBook && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">
                            {scannedBook.book?.title || scannedBook.title}
                          </p>
                          <p className="text-sm text-green-600">
                            {scannedBook.book?.authors?.join(', ') ||
                              scannedBook.authors?.join(', ')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step 2: Select Borrower */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Step 2: Scan RFID Card or Search Borrower
                  </h3>

                  {/* RFID Card Input */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={rfidCardInput}
                      onChange={(e) => setRfidCardInput(e.target.value)}
                      placeholder="Tap RFID card or enter card number..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleRFIDCardScan()}
                      autoFocus
                    />
                    <Button
                      onClick={handleRFIDCardScan}
                      disabled={rfidSearching || !rfidCardInput.trim()}
                      variant="outline"
                    >
                      <QrCodeIcon className="w-5 h-5 mr-2" />
                      {rfidSearching ? 'Scanning...' : 'Scan'}
                    </Button>
                  </div>

                  <div className="relative flex items-center mb-3">
                    <div className="flex-1 border-t border-gray-200"></div>
                    <span className="px-3 text-xs text-gray-500 bg-white">OR search by name</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>

                  <div className="relative">
                    <input
                      type="text"
                      value={borrowerSearch}
                      onChange={(e) => {
                        setBorrowerSearch(e.target.value)
                        searchBorrowers(e.target.value)
                      }}
                      placeholder="Search by name, admission number, or email..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {borrowerResults && borrowerResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {borrowerResults.map((user) => (
                          <button
                            key={user._id}
                            onClick={() => {
                              setSelectedBorrower(user)
                              setBorrowerSearch(`${user.firstName} ${user.lastName}`)
                              setBorrowerResults([])
                              // Auto-detect borrower type based on role
                              if ((user as any).role === 'STUDENT') setBorrowerType('student')
                              else if ((user as any).role === 'TEACHER') setBorrowerType('teacher')
                              else setBorrowerType('staff')
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                          >
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <UserIcon className="w-4 h-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(user as any).studentId || (user as any).email}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedBorrower && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-blue-800">
                            {selectedBorrower.firstName} {selectedBorrower.lastName}
                          </p>
                          <p className="text-sm text-blue-600">
                            {(selectedBorrower as any).studentId || (selectedBorrower as any).email}
                            {' '}<span className="capitalize">({borrowerType})</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowBorrowModal(false)
                      resetBorrowModal()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleBorrowBook}
                    disabled={!scannedBook || !selectedBorrower}
                  >
                    Complete Borrowing
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Return Book Modal */}
      <AnimatePresence>
        {showReturnModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowReturnModal(false)
              setReturnBarcode('')
              setReturnCondition('good')
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Return Book</h2>
                <button
                  onClick={() => {
                    setShowReturnModal(false)
                    setReturnBarcode('')
                    setReturnCondition('good')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Book Barcode
                  </label>
                  <input
                    type="text"
                    value={returnBarcode}
                    onChange={(e) => setReturnBarcode(e.target.value)}
                    placeholder="Scan or enter barcode..."
                    autoFocus
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Book Condition
                  </label>
                  <select
                    value={returnCondition}
                    onChange={(e) => setReturnCondition(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                    <option value="damaged">Damaged</option>
                  </select>
                  {['poor', 'damaged'].includes(returnCondition) && (
                    <p className="text-xs text-amber-600 mt-1">
                      A damage fine may be applied based on library policy
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowReturnModal(false)
                      setReturnBarcode('')
                      setReturnCondition('good')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleReturnBook} disabled={!returnBarcode.trim()}>
                    Return Book
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mark as Lost Modal */}
      <AnimatePresence>
        {showLostModal && borrowingToMarkLost && (() => {
          const lostBook = typeof borrowingToMarkLost.book === 'object' ? borrowingToMarkLost.book : null
          const lostBookCopy = typeof borrowingToMarkLost.bookCopy === 'object' ? borrowingToMarkLost.bookCopy : null
          const lostBorrower = typeof borrowingToMarkLost.borrower === 'object' ? borrowingToMarkLost.borrower : null
          return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowLostModal(false)
              setBorrowingToMarkLost(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with warning color */}
              <div className="bg-amber-50 px-6 py-4 border-b border-amber-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-full">
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Mark Book as Lost</h2>
                    <p className="text-sm text-amber-700">This action will issue a fine</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Book Details */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg border border-gray-200">
                      <BookOpenIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">
                        {lostBook?.title || 'Unknown Book'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Barcode: {lostBookCopy?.barcode || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Borrower: {lostBorrower?.firstName} {lostBorrower?.lastName}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning Message */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 mb-1">What happens when you mark this book as lost:</p>
                      <ul className="text-amber-700 space-y-1 list-disc list-inside">
                        <li>A replacement fine will be issued to the borrower</li>
                        <li>The book copy will be removed from circulation</li>
                        <li>This action cannot be undone</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowLostModal(false)
                      setBorrowingToMarkLost(null)
                    }}
                    disabled={markingAsLost}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleMarkAsLost}
                    disabled={markingAsLost}
                  >
                    {markingAsLost ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Confirm & Issue Fine'
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
          )
        })()}
      </AnimatePresence>
    </div>
  )
}

export default BorrowingsPage
